import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { UserPlus, Mic, Pencil, Trash2, Loader2, FileText, ShieldCheck, ShieldAlert, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listPatients, createPatient, updatePatient, deletePatient, errMsg } from "@/api";
import { PatientHistoryDialog } from "@/components/PatientHistoryDialog";
import { ConsentSignature } from "@/components/ConsentSignature";

const PatientForm = ({ trigger, initial, onSubmit, title }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initial?.name || "");
  const [reason, setReason] = useState(initial?.reason || "");
  // El consentimiento ya no se marca aquí: se firma en la pantalla de la tableta y
  // la ficha lo refleja. Se conserva el valor que tuviera para no borrarlo al editar.
  const aiConsent = initial?.ai_consent || false;
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ name, reason, ai_consent: aiConsent });
      setOpen(false);
      if (!initial) { setName(""); setReason(""); }
    } catch (err) {
      toast.error(errMsg(err, "Error al guardar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-brand-navy">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="p-name" className="text-brand-navy">Nombre del paciente</Label>
            <Input id="p-name" data-testid="patient-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" />
          </div>
          <div>
            <Label htmlFor="p-reason" className="text-brand-navy">Motivo de consulta</Label>
            <Input id="p-reason" data-testid="patient-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-11" />
          </div>
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 p-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 text-amber-700 shrink-0" />
            <span className="text-sm text-amber-900 leading-snug">
              {aiConsent ? (
                <>Esta paciente ya ha <strong>firmado</strong> el consentimiento.</>
              ) : (
                <>
                  El consentimiento <strong>se firma</strong>, no se marca aquí: guarda la ficha y
                  pulsa <strong>Firmar</strong> en su fila para pasarle la tableta.
                  <strong> Sin esa firma no se podrá grabar.</strong>
                </>
              )}
            </span>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="patient-save-button" disabled={saving} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PatientManager = ({ onRecord = null, refreshToken = 0 }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setPatients(await listPatients());
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron cargar los pacientes"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshToken]);

  const handleCreate = async (data) => { await createPatient(data); toast.success("Paciente añadido"); load(); };
  const handleUpdate = (id) => async (data) => { await updatePatient(id, data); toast.success("Paciente actualizado"); load(); };
  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name}?`)) return;
    try { await deletePatient(p.id); toast.success("Paciente eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // Paciente cuya firma se está recogiendo, y en qué modo.
  const [firmando, setFirmando] = useState(null);

  return (
    <div>
      {firmando && (
        <ConsentSignature
          patient={firmando.patient}
          modo={firmando.modo}
          onClose={() => setFirmando(null)}
          onDone={load}
        />
      )}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-brand-navy">Pacientes</h3>
        <PatientForm
          title="Nuevo paciente"
          onSubmit={handleCreate}
          trigger={<Button data-testid="add-patient-button" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white h-10"><UserPlus className="w-4 h-4" /> Añadir</Button>}
        />
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-brand-teal mx-auto" /></div>
      ) : patients.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center">No hay pacientes. Añade el primero.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="patients-list">
          {patients.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`patient-row-${p.id}`}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium text-brand-navy">{p.name}</p>
                <p className="text-sm text-slate-500">{p.reason || "—"}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Badge className="bg-slate-100 text-slate-600 border-0"><FileText className="w-3 h-3 mr-1" /> {p.notes_count} nota{p.notes_count === 1 ? "" : "s"}</Badge>
                  {p.ai_consent ? (
                    <Badge data-testid={`consent-ok-${p.id}`} className="bg-emerald-100 text-emerald-700 border-0"><ShieldCheck className="w-3 h-3 mr-1" /> Consentimiento IA</Badge>
                  ) : (
                    <Badge data-testid={`consent-missing-${p.id}`} className="bg-amber-100 text-amber-800 border-0"><ShieldAlert className="w-3 h-3 mr-1" /> Sin consentimiento IA</Badge>
                  )}
                  {p.fragments_count > 0 && (
                    <Badge data-testid={`open-session-${p.id}`} className="bg-amber-100 text-amber-800 border-0">
                      {p.fragments_count} nota{p.fragments_count === 1 ? "" : "s"} sin finalizar
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid={`sign-consent-${p.id}`}
                  variant="outline"
                  onClick={() => setFirmando({ patient: p, modo: p.ai_consent ? "revocar" : "firmar" })}
                  className="rounded-full h-10"
                  title={p.ai_consent
                    ? "Retirar el consentimiento, con su firma"
                    : "Pásale la tableta a la paciente para que lo lea y firme"}
                >
                  <PenLine className="w-4 h-4 mr-1" />
                  {p.ai_consent ? "Retirar" : "Firmar"}
                </Button>
                {onRecord && (
                  <Button
                    data-testid={`record-patient-${p.id}`}
                    onClick={() => onRecord(p)}
                    disabled={!p.ai_consent}
                    title={!p.ai_consent ? "Marca el consentimiento del paciente en su ficha para poder grabar" : ""}
                    className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white h-9 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Mic className="w-4 h-4" /> {p.fragments_count > 0 ? "Continuar sesión" : "Grabar"}
                  </Button>
                )}
                <PatientHistoryDialog patient={p} />
                <PatientForm
                  title="Editar paciente"
                  initial={p}
                  onSubmit={handleUpdate(p.id)}
                  trigger={<Button variant="outline" size="sm" data-testid={`edit-patient-${p.id}`} className="rounded-full border-slate-300 h-9"><Pencil className="w-4 h-4" /></Button>}
                />
                <Button variant="outline" size="sm" data-testid={`delete-patient-${p.id}`} onClick={() => handleDelete(p)} className="rounded-full border-slate-300 text-red-500 h-9"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
